import { useMemo, useCallback, useState } from 'react';
import {
  Checkbox,
  Menu,
  MenuToggle,
  MenuList,
  MenuItem,
  MenuGroup,
  Text,
  useColorStyle,
  useColorMode,
  MenuToggleProps,
} from '@tonic-one/react';
import { CaretDownIcon } from '@tonic-one/react-icons';
import { useTranslation } from 'react-i18next';
import { FeedbackRelatedEntry } from '../type';
import { RecommendedAction } from '../types/remediation';
import {
  getAllExecutableFeedbackEntries,
  isMitigationActionExecutable,
} from './utils';

interface SelectionCheckboxHeaderProps extends MenuToggleProps {
  rowSelection: { [key: string]: boolean };
  setRowSelection: (selection: { [key: string]: boolean }) => void;
  sortedData: FeedbackRelatedEntry[];
  fetchPage?: (
    offset: number,
    limit: number
  ) => Promise<{ contentList: FeedbackRelatedEntry[]; total: number }>;
  total: number;
  selectableTotal?: number;
  setSelectableTotal?: (count: number | undefined) => void;
  setAllPagesAction?: (action: RecommendedAction | null) => void;
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  dataGrid: {
    getIsAllPageRowsSelected: () => boolean;
    getIsAnyPageRowSelected: () => boolean;
    getIsAnyPageRowSelectable: () => boolean;
    getToggleAllPageRowsSelectedHandler: () => () => void;
  };
}

export const SelectionCheckboxHeader = ({
  rowSelection,
  setRowSelection,
  sortedData,
  fetchPage,
  total,
  selectableTotal,
  setSelectableTotal,
  setAllPagesAction,
  pagination,
  dataGrid,
  ...menuToggleProps
}: SelectionCheckboxHeaderProps) => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [isSelectingAllPages, setIsSelectingAllPages] = useState(false);

  const handleSelectAllCurrentPage = useCallback(() => {
    // `sortedData` already contains only the current page (server-side pagination).
    const currentPageData = sortedData;

    const newSelection = { ...rowSelection };
    currentPageData.forEach((row) => {
      if (isMitigationActionExecutable(row)) {
        newSelection[row.id] = true;
      }
    });
    setRowSelection(newSelection);
  }, [sortedData, rowSelection, setRowSelection]);

  const handleDeselectAllCurrentPage = useCallback(() => {
    // `sortedData` already contains only the current page (server-side pagination).
    const currentPageData = sortedData;

    const newSelection = { ...rowSelection };
    currentPageData.forEach((row) => {
      delete newSelection[row.id];
    });
    setRowSelection(newSelection);
  }, [sortedData, rowSelection, setRowSelection]);

  const handleSelectAllPages = useCallback(async () => {
    if (!fetchPage) {
      return;
    }
    setIsSelectingAllPages(true);
    try {
      const entries = await getAllExecutableFeedbackEntries(fetchPage);
      setSelectableTotal?.(entries.length);
      setAllPagesAction?.(entries[0]?.recommanded_action ?? null);
      setRowSelection(
        Object.fromEntries(entries.map((entry) => [entry.id, true]))
      );
    } catch {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setIsSelectingAllPages(false);
    }
  }, [fetchPage, setSelectableTotal, setAllPagesAction, setRowSelection, t]);

  const handleDeselectAllPages = useCallback(() => {
    setRowSelection({});
    setSelectableTotal?.(undefined);
    setAllPagesAction?.(null);
  }, [setRowSelection, setSelectableTotal, setAllPagesAction]);

  // Calculate selection states
  const selectionStates = useMemo(() => {
    // `sortedData` already contains only the current page (server-side pagination).
    const currentPageData = sortedData;

    const selectableCurrentPageData = currentPageData.filter((row) =>
      isMitigationActionExecutable(row)
    );

    // Check current page selection state
    const currentPageSelectedCount = selectableCurrentPageData.filter(
      (row) => rowSelection[row.id]
    ).length;

    const isCurrentPageAllSelected =
      selectableCurrentPageData.length > 0 &&
      currentPageSelectedCount === selectableCurrentPageData.length;
    const isCurrentPagePartiallySelected =
      currentPageSelectedCount > 0 &&
      currentPageSelectedCount < selectableCurrentPageData.length;

    const selectedCount = Object.values(rowSelection).filter(Boolean).length;
    const allPagesSelectableCount = selectableTotal ?? total;
    const isPartiallySelected =
      selectedCount > 0 && selectedCount < allPagesSelectableCount;
    const isAllSelected =
      allPagesSelectableCount > 0 && selectedCount === allPagesSelectableCount;

    return {
      isCurrentPageAllSelected,
      isCurrentPagePartiallySelected,
      isPartiallySelected,
      isAllSelected,
    };
  }, [sortedData, rowSelection, selectableTotal, total]);

  return (
    <Menu>
      <MenuToggle
        alignItems="center"
        disabled={total === 0}
        {...menuToggleProps}
      >
        <Checkbox
          checked={dataGrid.getIsAllPageRowsSelected()}
          indeterminate={
            dataGrid.getIsAnyPageRowSelected() &&
            !dataGrid.getIsAllPageRowsSelected()
          }
          disabled={total === 0}
          onChange={dataGrid.getToggleAllPageRowsSelectedHandler()}
          marginBottom="1h"
        />
        <CaretDownIcon
          size="4x"
          color={
            dataGrid.getIsAnyPageRowSelectable()
              ? colorStyle.color.primary
              : colorStyle.color.secondary
          }
        />
      </MenuToggle>
      <MenuList PopperProps={{ usePortal: true }}>
        <MenuGroup title={t('operations.euq.selection.currentPage')}>
          {!selectionStates.isCurrentPageAllSelected && (
            <MenuItem pl="6x" onClick={handleSelectAllCurrentPage}>
              {t('operations.euq.selection.selectAllCurrentPage')}
              <Text
                display="inline-flex"
                color={colorStyle.color.secondary}
                ml="1x"
              >
                ({pagination.pageSize})
              </Text>
            </MenuItem>
          )}
          {(selectionStates.isCurrentPagePartiallySelected ||
            selectionStates.isCurrentPageAllSelected) && (
            <MenuItem pl="6x" onClick={handleDeselectAllCurrentPage}>
              {t('operations.euq.selection.deselectAllCurrentPage')}
              <Text
                display="inline-flex"
                color={colorStyle.color.secondary}
                ml="1x"
              >
                ({pagination.pageSize})
              </Text>
            </MenuItem>
          )}
        </MenuGroup>
        <MenuGroup title={t('operations.euq.selection.allPages')}>
          <MenuItem
            pl="6x"
            onClick={handleSelectAllPages}
            disabled={isSelectingAllPages}
          >
            {t('operations.euq.selection.selectAllPages')}
            <Text
              display="inline-flex"
              color={colorStyle.color.secondary}
              ml="1x"
            >
              ({total})
            </Text>
          </MenuItem>
          {(selectionStates.isPartiallySelected ||
            selectionStates.isAllSelected) && (
            <MenuItem pl="6x" onClick={handleDeselectAllPages}>
              {t('operations.euq.selection.deselectAllPages')}
              <Text
                display="inline-flex"
                color={colorStyle.color.secondary}
                ml="1x"
              >
                ({total})
              </Text>
            </MenuItem>
          )}
        </MenuGroup>
      </MenuList>
    </Menu>
  );
};
